import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid } from "@material-ui/core";
import { Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import NestedTable from "../../../components/NestedTable";
import { commonStrings, dispatchInfoStrings } from "../../../components/StringBundle";
import { getVehicleInfoList, getVehicleTripList } from "./Service";
import { vehicleInfoMainStrings, vehicleTripsStrings } from "./constants";
import Snackbar from "../../../components/Snackbar";
import { dateFormat, mapObjectToArray } from "../../common";
import { useGlobalStyles } from "../../../components/globalStyles";
import { generateFilterPayload, transformData } from "../../../components/FilterUtils";
import { WarningRounded } from "@material-ui/icons";

// Key used to attach fetched trip rows onto each vehicle row object
const CHILD_DATA_KEY = "vehicleTrips";

// Page size options for the main table
const PAGE_SIZE_OPTIONS = [10, 25, 50];


const VehicleInformationTab = ({ dispatchId }) => {
  const globalClasses = useGlobalStyles();
  const location = useLocation();
  const gridID = "VehicleInfo";
  // sessionStorage keys — scoped to this pathname so different dispatch pages don't collide
  const STORAGE_KEY_FILTER   = `${gridID}.filters.${location.pathname}`;
  const STORAGE_KEY_PAGE     = `${gridID}.page.${location.pathname}`;
  const STORAGE_KEY_PAGESIZE = `${gridID}.pagesize`;

  // Restore state from sessionStorage on refresh — same pattern as DispatchNewList
  const savedPageSize = sessionStorage.getItem(STORAGE_KEY_PAGESIZE);
  const initialPageSize = savedPageSize ? parseInt(savedPageSize) : PAGE_SIZE_OPTIONS[0];
  const savedPage = sessionStorage.getItem(STORAGE_KEY_PAGE);
  const initialPage = savedPage ? parseInt(savedPage) : 1;
  const savedFilters = sessionStorage.getItem(STORAGE_KEY_FILTER);
  const initialFilters = savedFilters ? JSON.parse(savedFilters) : [];

  const vehicleInfoMainStrings = dispatchInfoStrings.vehicleInfoMain;
  const vehicleTripsStrings = dispatchInfoStrings.vehicleTrips;

  const [rows, setRows] = useState([]);

  const vehicleInfoColumns = useMemo(
    () => mapObjectToArray(vehicleInfoMainStrings, {}, "headerName", "field"),
    [rows]
  );

  const tripColumnsWithRender = useMemo(
    () => mapObjectToArray(vehicleTripsStrings, {}, "headerName", "field",),
    []
  );

  const [totalCount, setTotalCount] = useState(0);
  const [totalQuantityReceivedPct, setTotalQuantityReceivedPct] = useState(null);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(initialPage);
  const [pageLimit, setPageLimit] = useState(initialPageSize);

  const [filterSettings, setFilterSettings] = useState(initialFilters);
  const [showSnack, setSnack] = useState({});

  //payload formation — same structure as DispatchNewList.getSearchPayload
  function getSearchPayload(pageNum, limit, searchtext) {
    let finalSearchText = [...searchtext];
    return transformData(
      generateFilterPayload(finalSearchText, {
        page: pageNum,
        limit: limit,
        dispatch_id: dispatchId,
      })
    );
  }

  // list api call — same pattern as DispatchNewList.callListApi
  async function fetchVehicleInfoList(
    pageNum = page,
    limit = pageLimit,
    searchtext = filterSettings
  ) {
    if (!dispatchId) return;
    setLoading(true);
    try {
      const searchPayload = getSearchPayload(pageNum, limit, searchtext);
      const res = await getVehicleInfoList(searchPayload);
      setTotalCount(res?.total_count ?? 0);
      setTotalQuantityReceivedPct(
        res?.total_quantity_received_percentage != null ? `${res.total_quantity_received_percentage}%` : 0
      );
      // Preserve already-fetched trip data across pagination / filter changes
      setRows((prev) => {
        const prevMap = {};
        prev.forEach((r) => { prevMap[r.record_id] = r[CHILD_DATA_KEY]; });
        return (res?.vehicle_info ?? []).map((r) => {
          const preserved = prevMap[r.record_id];
          return preserved !== undefined ? { ...r, [CHILD_DATA_KEY]: preserved } : r;
        });
      });
    } catch (e) {
      console.error(e);
      setRows([]);
      setSnack({
        open: true,
        message: commonStrings.serverError,
        severity: commonStrings.error,
      });
    } finally {
      setLoading(false);
    }
  }

  // Initial load — also restores persisted page/filters on refresh
  useEffect(() => {
    fetchVehicleInfoList(initialPage, initialPageSize, initialFilters);
  }, [dispatchId]);

  // Lazy-load trip rows when a vehicle row is expanded
  async function getChildData(row) {
    try {
      const trips = await getVehicleTripList(row.record_id);
      setRows((prev) =>
        prev.map((r) => (r.record_id === row.record_id ? { ...r, [CHILD_DATA_KEY]: trips } : r))
      );
    } catch (e) {
      console.error(e);
      setRows((prev) =>
        prev.map((r) => (r.record_id === row.record_id ? { ...r, [CHILD_DATA_KEY]: [] } : r))
      );
    }
  }

  //Pagination change — persists to sessionStorage (same as DispatchNewList.paginationChange)
  function handlePageChange(newPage) {
    setPage(newPage);
    sessionStorage.setItem(STORAGE_KEY_PAGE, newPage);
    fetchVehicleInfoList(newPage, pageLimit, filterSettings);
  }

  function handlePageSizeChange(newSize) {
    setPageLimit(newSize);
    setPage(1);
    sessionStorage.setItem(STORAGE_KEY_PAGESIZE, newSize);
    sessionStorage.setItem(STORAGE_KEY_PAGE, 1);
    fetchVehicleInfoList(1, newSize, filterSettings);
  }

  //Filter table api calls — same as DispatchNewList.filterDataColumn
  function filterDataColumn(searchArr) {
    const val = searchArr === "" ? [] : searchArr;
    setFilterSettings(val);
    setPage(1);
    sessionStorage.setItem(STORAGE_KEY_FILTER, JSON.stringify(val));
    sessionStorage.setItem(STORAGE_KEY_PAGE, 1);
    fetchVehicleInfoList(1, pageLimit, val);
  }

  // Snackbar close function
  const snackbarClose = () => {
    setSnack({ open: false, message: "", severity: "" });
  };

  const paginationParams = {
    page,
    pageLimit,
    totalCount,
    onPageChange: handlePageChange,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    onPageSizeChange: handlePageSizeChange,
  };

  return (
    <>
      {showSnack.open && (
        <Snackbar {...showSnack} handleClose={snackbarClose} />
      )}
      <>
        <Grid container style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Grid item md={8} >
            <span className={globalClasses.addBtnNote}>
              <WarningRounded className={globalClasses.addBtnNoteIcon} />
              {dispatchInfoStrings.vehicle.vehicalNoticeMSg}
            </span>
          </Grid>
          <Grid item md={4} >
            <Box className={`${globalClasses.paramButtonPosition} ${globalClasses.gap30} ${globalClasses.tabletopmarginright}`}>
              <Typography variant="subtitle2">{dispatchInfoStrings.view.totalQuantityReceivedPct}</Typography>
              <Typography variant="body2">{totalQuantityReceivedPct}</Typography>
            </Box>
          </Grid>
        </Grid>
        <NestedTable
          data={rows}
          mainRowCols={vehicleInfoColumns}
          subRowCols={tripColumnsWithRender}
          subHeaderTitle={dispatchInfoStrings.view.vehicleMovementSubHeader}
          childDataKey={CHILD_DATA_KEY}
          childApiCall
          getChildData={getChildData}
          isLoading={loading}
          headerkey={"vehicle_no"}
          paginationParams={paginationParams}
          enableFilters
          filterDataColumn={filterDataColumn}
          id={gridID}
        />
      </>
    </>
  );
};

export default VehicleInformationTab;
